import * as THREE from 'three';
import {
  BlendMode,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
} from 'three/webgpu';
import {
  instancedBufferAttribute,
  mrt,
  normalLocal,
  output,
  positionLocal,
  storage,
  transformNormalToView,
  uint,
  varying,
  vec4,
} from 'three/tsl';
import { PhysicsEngine } from '../physics/PhysicsEngine';
import { registerGtaoPresentationOverlayMaterial } from './gtaoPresentationOverlay';

export interface EmitterRuntimeConfig {
  enabled: boolean;
  rate: number; // bodies per second
  x: number;
  y: number;
  z: number;
  randomRotation: boolean;
  rotationJitter: number;
}

export interface SpawnConfig {
  count: number; // capacity
  initialCount?: number;
  halfExtents: [number, number, number];
  layout?: 'random' | 'column' | 'pyramid' | 'pyramid-wall' | 'grid';
  spawnArea?: {
    x: [number, number];
    y: [number, number];
    z: [number, number];
  };
  column?: {
    x: number;
    z: number;
    startY: number;
    spacing: number;
    offsetXPerLevel?: number;
    offsetZPerLevel?: number;
  };
  pyramid?: {
    x: number;
    z: number;
    startY: number;
    layers: number;
    spacing: number;
  };
  pyramidWall?: {
    x: number;
    z: number;
    startY: number;
    layers: number;
    stepX?: number;
    stepY?: number;
  };
  grid?: {
    x: number;
    z: number;
    startY: number;
    sizeX: number;
    sizeY: number;
    sizeZ: number;
    spacing: number;
    spacingX?: number;
    spacingY?: number;
    spacingZ?: number;
    stacksX?: number;
    stacksZ?: number;
    stackSpacingX?: number;
    stackSpacingZ?: number;
  };
  randomRotation?: boolean;
  rotationJitter?: number;
  emitter?: {
    enabled?: boolean;
    rate?: number;
  };
}

export interface BoxSpawner {
  instancedMesh: THREE.InstancedMesh;
  syncVisuals(engine: PhysicsEngine): void;
  update(dt: number): void;
  emit(requestedCount?: number): number;
  shoot(config: {
    position: [number, number, number];
    direction: [number, number, number];
    averageSpeed: number;
    speedJitter?: number;
    angularJitter?: number;
  }): boolean;
  getSpawnedCount(): number;
  getCapacity(): number;
  getEmitter(): EmitterRuntimeConfig;
  setEmitter(patch: Partial<EmitterRuntimeConfig>): void;
  dispose(): void;
}

export function createBoxSpawner(
  scene: THREE.Object3D,
  physics: PhysicsEngine,
  config: SpawnConfig,
): BoxSpawner {
  const { count, halfExtents } = config;
  const layout = config.layout ?? 'random';
  const spawnArea = config.spawnArea ?? {
    x: [-5, 5] as [number, number],
    y: [2, 20] as [number, number],
    z: [-5, 5] as [number, number],
  };
  const column = config.column ?? {
    x: 0,
    z: 0,
    startY: 2,
    spacing: halfExtents[1] * 2 + 0.05,
    offsetXPerLevel: 0,
    offsetZPerLevel: 0,
  };
  const pyramid = config.pyramid ?? {
    x: 0,
    z: 0,
    startY: halfExtents[1],
    layers: 6,
    spacing: 0.02,
  };
  const pyramidWall = config.pyramidWall ?? {
    x: 0,
    z: 0,
    startY: halfExtents[1],
    layers: 8,
    stepX: halfExtents[0] * 2 + 0.03,
    stepY: halfExtents[1] * 2 * 0.85,
  };
  const grid = config.grid ?? {
    x: 0,
    z: 0,
    startY: halfExtents[1],
    sizeX: 10,
    sizeY: 10,
    sizeZ: 10,
    spacing: 0.02,
    stacksX: 1,
    stacksZ: 1,
    stackSpacingX: 0.0,
    stackSpacingZ: 0.0,
  };
  const [hx, hy, hz] = halfExtents;
  const pyramidLayers = Math.max(1, Math.floor(pyramid.layers));
  const pyramidStepX = hx * 2 + pyramid.spacing;
  const pyramidStepZ = hz * 2 + pyramid.spacing;
  const pyramidLayerHeight = hy * 2 + pyramid.spacing;
  const pyramidWallLayers = Math.max(1, Math.floor(pyramidWall.layers));
  const pyramidWallStepX = pyramidWall.stepX ?? (hx * 2 + 0.03);
  const pyramidWallStepY = pyramidWall.stepY ?? (hy * 2 * 0.85);
  const gridSizeX = Math.max(1, Math.floor(grid.sizeX));
  const gridSizeY = Math.max(1, Math.floor(grid.sizeY));
  const gridSizeZ = Math.max(1, Math.floor(grid.sizeZ));
  const gridStepX = hx * 2 + (grid.spacingX ?? grid.spacing);
  const gridStepY = hy * 2 + (grid.spacingY ?? grid.spacing);
  const gridStepZ = hz * 2 + (grid.spacingZ ?? grid.spacing);
  const gridStacksX = Math.max(1, Math.floor(grid.stacksX ?? 1));
  const gridStacksZ = Math.max(1, Math.floor(grid.stacksZ ?? 1));
  const gridStackStepX = gridSizeX * gridStepX + (grid.stackSpacingX ?? 0.0);
  const gridStackStepZ = gridSizeZ * gridStepZ + (grid.stackSpacingZ ?? 0.0);
  const pyramidBaseCells = Math.floor(
    (pyramidLayers * (pyramidLayers + 1) * (2 * pyramidLayers + 1)) / 6,
  );
  const pyramidWallBaseCells = Math.floor((pyramidWallLayers * (pyramidWallLayers + 1)) / 2);
  const defaultRandomRotation = config.randomRotation ?? (layout === 'random');
  const defaultRotationJitter = Math.max(config.rotationJitter ?? 0.08, 0.0);
  const initialCount = Math.min(Math.max(config.initialCount ?? count, 0), count);

  const emitter: EmitterRuntimeConfig = {
    enabled: config.emitter?.enabled ?? false,
    rate: Math.max(config.emitter?.rate ?? 24.0, 0.0),
    x: column.x,
    y: column.startY,
    z: column.z,
    randomRotation: defaultRandomRotation,
    rotationJitter: defaultRotationJitter,
  };

  const geometry = new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
  const material = new MeshStandardNodeMaterial();
  material.color.setHex(0xe07020);
  material.roughness = 0.6;
  material.metalness = 0.1;
  const instancedMesh = new THREE.InstancedMesh(geometry, material, count);
  instancedMesh.castShadow = true;
  instancedMesh.receiveShadow = true;
  // Physics bodies can move outside the original center bounds quickly.
  instancedMesh.frustumCulled = false;
  // The storage-backed position/quaternion nodes are attached by syncVisuals().
  // Hide the unbound instances during async startup instead of drawing every
  // initial box at the geometry origin.
  instancedMesh.visible = false;
  instancedMesh.count = 0;
  scene.add(instancedMesh);

  const outlineMaterial = new MeshBasicNodeMaterial();
  outlineMaterial.color.setHex(0x111111);
  outlineMaterial.wireframe = true;
  outlineMaterial.transparent = true;
  outlineMaterial.opacity = 0.55;
  outlineMaterial.depthWrite = false;
  // Transparent presentation helpers are not geometry guides. Their color is
  // still blended into the scene output, but a zero-alpha normal attachment
  // preserves the opaque normal already stored behind them. Publishing an
  // outline normal here would pair it with the opaque depth (depthWrite=false)
  // and create false GTAO halos along every wireframe edge.
  const outlineMrt = mrt({ output, normal: vec4(0, 0, 0, 0) });
  outlineMrt.setBlendMode('normal', new BlendMode(THREE.MaterialBlending));
  registerGtaoPresentationOverlayMaterial(outlineMaterial, outlineMrt);
  const outlineMesh = new THREE.InstancedMesh(geometry, outlineMaterial, count);
  outlineMesh.frustumCulled = false;
  outlineMesh.visible = false;
  outlineMesh.count = 0;
  outlineMesh.renderOrder = 1;
  // A second full-scene wireframe pass is disproportionately expensive for
  // dense GPU stress scenes and provides no physical information. Keep it for
  // small inspection scenes, and use the shaded instanced pass alone above the
  // detail threshold.
  const showOutline = count <= 1000;
  if (showOutline) scene.add(outlineMesh);

  const bodyIndexData = new Float32Array(count * 4);
  const bodyIndexAttr = new THREE.InstancedBufferAttribute(bodyIndexData, 4);
  const tmpQuat = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const outlineScale = 1.002;

  let spawnedCount = 0;
  let emitterAccumulator = 0.0;
  let emitterSlotSequence = 0;
  let gpuVisualsBound = false;

  // Keep emitted bodies out of the exact same pose. Bodies are assigned to
  // unique 5 x 5 tiles in a bounded, near-cubic staging lattice shared by all
  // emit() calls. This preserves a compact nozzle for continuous emission but
  // avoids the former O(count) vertical tower (64k boxes could start more than
  // a kilometre above the requested emitter), which injected extreme gravity
  // energy and damaged BVH scale. The lattice never wraps within capacity.
  const emitterNozzleSide = 5;
  const emitterNozzleSlots = emitterNozzleSide * emitterNozzleSide;
  const emitterClearance = 0.06;
  const emitterStepX = hx * 2 + emitterClearance;
  const emitterStepY = hy * 2 + emitterClearance;
  const emitterStepZ = hz * 2 + emitterClearance;
  const emitterRotationSafeStep = Math.hypot(hx, hy, hz) * 2 + emitterClearance;
  const emitterLatticeStepX = Math.max(emitterStepX, emitterRotationSafeStep);
  const emitterLatticeStepY = Math.max(emitterStepY, emitterRotationSafeStep);
  const emitterLatticeStepZ = Math.max(emitterStepZ, emitterRotationSafeStep);
  const emitterTileGroups = Math.max(1, Math.ceil(count / emitterNozzleSlots));
  // A tile is five cells wide in X/Z but one cell high, so balancing physical
  // extents gives tileSide ~= cbrt(groupCount / 5). Use an odd side so the
  // first tile is exactly centred on the configured emitter.
  const emitterRawTileSide = Math.max(1, Math.ceil(Math.cbrt(emitterTileGroups / emitterNozzleSide)));
  const emitterTileRadius = Math.ceil((emitterRawTileSide - 1) * 0.5);
  const emitterTileCoordinates: Array<[number, number]> = [];
  for (let tileZ = -emitterTileRadius; tileZ <= emitterTileRadius; tileZ++) {
    for (let tileX = -emitterTileRadius; tileX <= emitterTileRadius; tileX++) {
      emitterTileCoordinates.push([tileX, tileZ]);
    }
  }
  emitterTileCoordinates.sort((a, b) => {
    const shellA = Math.max(Math.abs(a[0]), Math.abs(a[1]));
    const shellB = Math.max(Math.abs(b[0]), Math.abs(b[1]));
    if (shellA !== shellB) return shellA - shellB;
    const distanceA = a[0] * a[0] + a[1] * a[1];
    const distanceB = b[0] * b[0] + b[1] * b[1];
    if (distanceA !== distanceB) return distanceA - distanceB;
    return a[1] - b[1] || a[0] - b[0];
  });

  const spawnBody = (
    position: [number, number, number],
    quaternion: [number, number, number, number],
    linearVelocity?: [number, number, number],
    angularVelocity?: [number, number, number],
  ): boolean => {
    if (spawnedCount >= count || physics.getBodyCount() >= physics.config.maxBodies) {
      return false;
    }

    const bodyIndex = physics.addBody({
      position,
      quaternion,
      mass: 1.0,
      halfExtents: [hx, hy, hz],
      linearVelocity,
      angularVelocity,
    });

    bodyIndexData[spawnedCount * 4 + 0] = bodyIndex;
    bodyIndexAttr.needsUpdate = true;

    spawnedCount++;
    instancedMesh.count = spawnedCount;
    if (showOutline) outlineMesh.count = spawnedCount;
    return true;
  };

  const makeSpawnQuaternion = (forEmitter: boolean): [number, number, number, number] => {
    if (emitter.randomRotation) {
      if (forEmitter || layout === 'column') {
        const jitter = Math.max(emitter.rotationJitter, 0.0);
        euler.set(
          THREE.MathUtils.randFloatSpread(jitter * 2),
          THREE.MathUtils.randFloatSpread(jitter * 2),
          THREE.MathUtils.randFloatSpread(jitter * 2),
        );
      } else {
        euler.set(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
        );
      }
      tmpQuat.setFromEuler(euler);
    } else {
      tmpQuat.set(0, 0, 0, 1);
    }
    return [tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w];
  };

  const spawnOneFromLayout = (): boolean => {
    let x = 0;
    let y = 0;
    let z = 0;

    if (layout === 'column') {
      const columnIndex = spawnedCount;
      const offsetXPerLevel = column.offsetXPerLevel ?? 0;
      const offsetZPerLevel = column.offsetZPerLevel ?? 0;
      x = column.x + columnIndex * offsetXPerLevel;
      y = column.startY + columnIndex * column.spacing;
      z = column.z + columnIndex * offsetZPerLevel;
    } else if (layout === 'pyramid') {
      let localIndex = spawnedCount;
      let layer = 0;
      let side = pyramidLayers;
      while (side > 0) {
        const layerCells = side * side;
        if (localIndex < layerCells) {
          break;
        }
        localIndex -= layerCells;
        layer++;
        side--;
      }

      if (side <= 0) {
        const overflow = spawnedCount - pyramidBaseCells;
        x = pyramid.x;
        z = pyramid.z;
        y = pyramid.startY + pyramidLayers * pyramidLayerHeight + overflow * pyramidLayerHeight;
      } else {
        const ix = localIndex % side;
        const iz = Math.floor(localIndex / side);
        const offset = (side - 1) * 0.5;
        x = pyramid.x + (ix - offset) * pyramidStepX;
        z = pyramid.z + (iz - offset) * pyramidStepZ;
        y = pyramid.startY + layer * pyramidLayerHeight;
      }
    } else if (layout === 'pyramid-wall') {
      let localIndex = spawnedCount;
      let layer = 0;
      let rowCount = pyramidWallLayers;
      while (rowCount > 0) {
        if (localIndex < rowCount) {
          break;
        }
        localIndex -= rowCount;
        layer++;
        rowCount--;
      }

      if (rowCount <= 0) {
        const overflow = spawnedCount - pyramidWallBaseCells;
        x = pyramidWall.x;
        z = pyramidWall.z;
        y = pyramidWall.startY + pyramidWallLayers * pyramidWallStepY + overflow * (hy * 2 + 0.03);
      } else {
        x = pyramidWall.x
          + localIndex * pyramidWallStepX
          + layer * (0.5 * pyramidWallStepX)
          - pyramidWallLayers * (0.5 * pyramidWallStepX);
        y = pyramidWall.startY + layer * pyramidWallStepY;
        z = pyramidWall.z;
      }
    } else if (layout === 'grid') {
      const gridBaseCells = gridSizeX * gridSizeY * gridSizeZ;
      const stacksPerBand = gridStacksX * gridStacksZ;
      const gridBandCells = gridBaseCells * stacksPerBand;
      const stackBand = Math.floor(spawnedCount / gridBandCells);
      const bandLocalIndex = spawnedCount - stackBand * gridBandCells;
      const stackIndex = Math.floor(bandLocalIndex / gridBaseCells);
      const localIndex = bandLocalIndex - stackIndex * gridBaseCells;
      const stackX = stackIndex % gridStacksX;
      const stackZ = Math.floor(stackIndex / gridStacksX);
      const plane = gridSizeX * gridSizeZ;
      const gy = Math.floor(localIndex / plane);
      const rem = localIndex - gy * plane;
      const gz = Math.floor(rem / gridSizeX);
      const gx = rem - gz * gridSizeX;
      const offsetX = (gridSizeX - 1) * 0.5;
      const offsetZ = (gridSizeZ - 1) * 0.5;
      const offsetStackX = (gridStacksX - 1) * 0.5;
      const offsetStackZ = (gridStacksZ - 1) * 0.5;
      x = grid.x
        + (stackX - offsetStackX) * gridStackStepX
        + (gx - offsetX) * gridStepX;
      z = grid.z
        + (stackZ - offsetStackZ) * gridStackStepZ
        + (gz - offsetZ) * gridStepZ;
      y = grid.startY + (stackBand * gridSizeY + gy) * gridStepY;
    } else {
      x = THREE.MathUtils.randFloat(spawnArea.x[0], spawnArea.x[1]);
      y = THREE.MathUtils.randFloat(spawnArea.y[0], spawnArea.y[1]);
      z = THREE.MathUtils.randFloat(spawnArea.z[0], spawnArea.z[1]);
    }

    const quat = makeSpawnQuaternion(false);
    return spawnBody([x, y, z], quat, undefined, undefined);
  };

  const spawnOneFromEmitter = (): boolean => {
    const slot = emitterSlotSequence % emitterNozzleSlots;
    const tileSequence = Math.floor(emitterSlotSequence / emitterNozzleSlots);
    const tileIndex = tileSequence % emitterTileCoordinates.length;
    const layer = Math.floor(tileSequence / emitterTileCoordinates.length);
    const [tileX, tileZ] = emitterTileCoordinates[tileIndex]!;
    const slotX = slot % emitterNozzleSide;
    const slotZ = Math.floor(slot / emitterNozzleSide);
    const nozzleCenter = (emitterNozzleSide - 1) * 0.5;
    // Use one immutable rotation-safe lattice scale. Switching randomRotation
    // at runtime must not remap later integer cells onto positions already
    // occupied under a different scale.
    const x = emitter.x + (tileX * emitterNozzleSide + slotX - nozzleCenter) * emitterLatticeStepX;
    const y = emitter.y + layer * emitterLatticeStepY;
    const z = emitter.z + (tileZ * emitterNozzleSide + slotZ - nozzleCenter) * emitterLatticeStepZ;
    const quat = makeSpawnQuaternion(true);
    if (!spawnBody([x, y, z], quat, undefined, undefined)) {
      return false;
    }
    emitterSlotSequence++;
    return true;
  };

  const emitByLayout = (requestedCount = 1): number => {
    const toEmit = Math.max(0, Math.floor(requestedCount));
    return physics.withMutationBatch(() => {
      let emitted = 0;
      for (let i = 0; i < toEmit; i++) {
        if (!spawnOneFromLayout()) {
          break;
        }
        emitted++;
      }
      return emitted;
    });
  };

  const emitFromEmitter = (requestedCount = 1): number => {
    const toEmit = Math.max(0, Math.floor(requestedCount));
    return physics.withMutationBatch(() => {
      let emitted = 0;
      for (let i = 0; i < toEmit; i++) {
        if (!spawnOneFromEmitter()) {
          break;
        }
        emitted++;
      }
      return emitted;
    });
  };

  if (initialCount > 0) {
    emitByLayout(initialCount);
  }

  return {
    instancedMesh,

    syncVisuals(engine: PhysicsEngine): void {
      if (gpuVisualsBound) return;

      const renderBuffers = engine.getRenderBuffers();
      if (!renderBuffers) return;

      const positions = storage(renderBuffers.positions, 'vec4', engine.config.maxBodies).toReadOnly();
      const quaternions = storage(renderBuffers.quaternions, 'vec4', engine.config.maxBodies).toReadOnly();
      const bodyIndices = instancedBufferAttribute(bodyIndexAttr, 'vec4');
      const bodyIdx = uint((bodyIndices as any).x);
      const pos = positions.element(bodyIdx).xyz;
      const quat = quaternions.element(bodyIdx);
      const qv = quat.xyz;
      const qw = quat.w;

      const rotateByQuat = (v: any) => {
        const t = qv.cross(v).mul(2.0);
        return v.add(t.mul(qw)).add(qv.cross(t));
      };

      material.positionNode = rotateByQuat(positionLocal).add(pos);
      // NodeMaterial expects custom normalNode in view space.
      material.normalNode = varying(
        transformNormalToView(rotateByQuat(normalLocal)).normalize(),
        'vSpawnerBodyNormalView',
      );
      material.needsUpdate = true;

      if (showOutline) {
        outlineMaterial.positionNode = rotateByQuat(positionLocal.mul(outlineScale)).add(pos);
        outlineMaterial.needsUpdate = true;
      }
      instancedMesh.visible = true;
      if (showOutline) outlineMesh.visible = true;
      gpuVisualsBound = true;
    },

    update(dt: number): void {
      if (!emitter.enabled || emitter.rate <= 0.0 || spawnedCount >= count) {
        return;
      }

      emitterAccumulator += Math.max(0.0, dt) * emitter.rate;
      const toEmit = Math.floor(emitterAccumulator);
      if (toEmit <= 0) {
        return;
      }

      emitterAccumulator -= toEmit;
      const emitted = emitFromEmitter(toEmit);
      if (emitted < toEmit || spawnedCount >= count) {
        emitter.enabled = false;
        emitterAccumulator = 0.0;
      }
    },

    emit(requestedCount = 1): number {
      return emitFromEmitter(requestedCount);
    },

    shoot(config): boolean {
      const dir = new THREE.Vector3(config.direction[0], config.direction[1], config.direction[2]);
      if (dir.lengthSq() < 1e-8) {
        return false;
      }

      dir.normalize();
      const speedJitter = Math.max(config.speedJitter ?? 0.0, 0.0);
      const averageSpeed = Math.max(config.averageSpeed, 0.0);
      const speed = Math.max(0.0, averageSpeed + THREE.MathUtils.randFloatSpread(speedJitter * 2));
      const velocity = dir.multiplyScalar(speed);
      const angularJitter = Math.max(config.angularJitter ?? 0.0, 0.0);
      const angularVelocity: [number, number, number] = [
        THREE.MathUtils.randFloatSpread(angularJitter * 2),
        THREE.MathUtils.randFloatSpread(angularJitter * 2),
        THREE.MathUtils.randFloatSpread(angularJitter * 2),
      ];

      return spawnBody(
        config.position,
        [0, 0, 0, 1],
        [velocity.x, velocity.y, velocity.z],
        angularVelocity,
      );
    },

    getSpawnedCount(): number {
      return spawnedCount;
    },

    getCapacity(): number {
      return count;
    },

    getEmitter(): EmitterRuntimeConfig {
      return { ...emitter };
    },

    setEmitter(patch: Partial<EmitterRuntimeConfig>): void {
      if (patch.enabled !== undefined) emitter.enabled = patch.enabled;
      if (patch.rate !== undefined) emitter.rate = Math.max(0.0, patch.rate);
      if (patch.x !== undefined) emitter.x = patch.x;
      if (patch.y !== undefined) emitter.y = patch.y;
      if (patch.z !== undefined) emitter.z = patch.z;
      if (patch.randomRotation !== undefined) emitter.randomRotation = patch.randomRotation;
      if (patch.rotationJitter !== undefined) emitter.rotationJitter = Math.max(0.0, patch.rotationJitter);
    },

    dispose(): void {
      scene.remove(instancedMesh);
      if (showOutline) scene.remove(outlineMesh);
      instancedMesh.removeFromParent();
      outlineMesh.removeFromParent();
      instancedMesh.dispose();
      outlineMesh.dispose();
      geometry.dispose();
      material.dispose();
      outlineMaterial.dispose();
    },
  };
}
